import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Plus, Trash2, Copy, Loader2, Clock, CalendarDays, Users, Edit3, Power, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Panel, EmptyState } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const DOW = [
  { v: 0, label: "Mon" }, { v: 1, label: "Tue" }, { v: 2, label: "Wed" },
  { v: 3, label: "Thu" }, { v: 4, label: "Fri" }, { v: 5, label: "Sat" }, { v: 6, label: "Sun" },
];

const DEFAULT = {
  name: "30 min meeting", description: "", duration_minutes: 30, buffer_minutes: 10,
  lead_time_hours: 2, horizon_days: 14, max_per_day: 6,
  business_hours_start: "09:00", business_hours_end: "17:00",
  days_of_week: [0, 1, 2, 3, 4], is_active: true,
};

export default function Availability() {
  const [links, setLinks] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/availability"); setLinks(data); }
    catch (e) { toast.error(errMsg(e)); setLinks([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.name}"? Existing bookings stay on your calendar.`)) return;
    try { await api.delete(`/availability/${l.link_id}`); toast.success("Link deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleActive = async (l) => {
    try { await api.patch(`/availability/${l.link_id}`, { ...l, is_active: !l.is_active }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const copyLink = (l) => {
    const url = `${window.location.origin}/book/${l.slug}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied"), () => toast.error("Copy failed"));
  };

  return (
    <div data-testid="availability-page">
      <PageHeader title="Share availability" subtitle="Create a public link where people can book time on your calendar. Slots respect every connected calendar."
        actions={<Button onClick={() => setEditing({ ...DEFAULT })} data-testid="availability-new-button"><Plus className="h-4 w-4 mr-1.5" />New link</Button>} />
      {links === null ? <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
        : links.length === 0 ? (
          <EmptyState icon={Link2} title="No booking links yet"
            text="Create a shareable link visitors use to book a free time on your calendar."
            action={<Button onClick={() => setEditing({ ...DEFAULT })} data-testid="availability-empty-new-button"><Plus className="h-4 w-4 mr-1.5" />Create your first link</Button>} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="availability-grid">
            {links.map((l) => {
              const url = `${window.location.origin}/book/${l.slug}`;
              return (
                <div key={l.link_id} className="bg-card border border-border rounded-xl p-5 flex flex-col" data-testid="availability-card">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0"><p className="font-semibold truncate">{l.name}</p>
                      <p className="text-xs text-muted-foreground truncate min-h-[1rem]">{l.description || "\u2014"}</p>
                    </div>
                    <Badge variant={l.is_active ? "default" : "secondary"}>{l.is_active ? "Live" : "Paused"}</Badge>
                  </div>
                  <ul className="mt-3 space-y-1 text-xs text-muted-foreground flex-1">
                    <li className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{l.duration_minutes} min &middot; {l.business_hours_start}&ndash;{l.business_hours_end}</li>
                    <li className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />Next {l.horizon_days} days &middot; up to {l.max_per_day}/day</li>
                    <li className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{l.bookings_count || 0} booking{l.bookings_count === 1 ? "" : "s"}</li>
                  </ul>
                  <div className="mt-3 flex items-center gap-1 overflow-hidden bg-muted/50 rounded-lg">
                    <code className="text-[11px] font-mono truncate flex-1 px-2 py-1.5" data-testid="availability-share-url">{url}</code>
                    <Button variant="ghost" size="icon" onClick={() => copyLink(l)} aria-label="Copy" data-testid="availability-copy-button"><Copy className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => window.open(url, "_blank")} aria-label="Open" data-testid="availability-open-button"><ExternalLink className="h-3.5 w-3.5" /></Button>
                  </div>
                  <div className="mt-3 flex items-center gap-1">
                    <Button variant="secondary" size="sm" onClick={() => setEditing(l)} data-testid="availability-edit-button"><Edit3 className="h-3.5 w-3.5 mr-1" />Edit</Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleActive(l)} data-testid="availability-pause-button"><Power className="h-3.5 w-3.5 mr-1" />{l.is_active ? "Pause" : "Resume"}</Button>
                    <Button variant="ghost" size="sm" className="text-destructive ml-auto" onClick={() => remove(l)} data-testid="availability-delete-button"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      {editing && <LinkEditor link={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function LinkEditor({ link, onClose, onSaved }) {
  const [f, setF] = useState(link);
  const [busy, setBusy] = useState(false);
  const isNew = !link.link_id;

  const toggleDay = (v) => setF((x) => ({ ...x, days_of_week: x.days_of_week.includes(v) ? x.days_of_week.filter((d) => d !== v) : [...x.days_of_week, v].sort() }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Give the link a name");
    if (f.business_hours_end <= f.business_hours_start) return toast.error("Hours end must be after start");
    if (f.days_of_week.length === 0) return toast.error("Pick at least one day");
    setBusy(true);
    try {
      const payload = { ...f, duration_minutes: Number(f.duration_minutes), buffer_minutes: Number(f.buffer_minutes),
        lead_time_hours: Number(f.lead_time_hours), horizon_days: Number(f.horizon_days), max_per_day: Number(f.max_per_day) };
      if (isNew) await api.post("/availability", payload);
      else await api.patch(`/availability/${link.link_id}`, payload);
      toast.success(isNew ? "Link created" : "Link updated");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="availability-editor">
        <DialogHeader><DialogTitle>{isNew ? "New booking link" : "Edit booking link"}</DialogTitle>
          <DialogDescription>Visitors see this public page: no sign-in required to book.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="30 min intro call" data-testid="availability-name-input" />
          </div>
          <div className="space-y-1.5"><Label>Description (optional)</Label>
            <Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="A quick chat to see if we're a good fit." data-testid="availability-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Duration (minutes)</Label>
              <Select value={String(f.duration_minutes)} onValueChange={(v) => setF({ ...f, duration_minutes: Number(v) })}>
                <SelectTrigger data-testid="availability-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[15, 20, 30, 45, 60, 90, 120].map((n) => <SelectItem key={n} value={String(n)}>{n} min</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Buffer between meetings</Label>
              <Select value={String(f.buffer_minutes)} onValueChange={(v) => setF({ ...f, buffer_minutes: Number(v) })}>
                <SelectTrigger data-testid="availability-buffer-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[0, 5, 10, 15, 30, 60].map((n) => <SelectItem key={n} value={String(n)}>{n} min</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Business hours start</Label>
              <Input type="time" value={f.business_hours_start} onChange={(e) => setF({ ...f, business_hours_start: e.target.value })} data-testid="availability-start-input" />
            </div>
            <div className="space-y-1.5"><Label>Business hours end</Label>
              <Input type="time" value={f.business_hours_end} onChange={(e) => setF({ ...f, business_hours_end: e.target.value })} data-testid="availability-end-input" />
            </div>
          </div>
          <div>
            <Label>Days of the week</Label>
            <div className="mt-2 flex gap-1 flex-wrap">
              {DOW.map((d) => (
                <button type="button" key={d.v} onClick={() => toggleDay(d.v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${f.days_of_week.includes(d.v) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-foreground hover:bg-muted"}`}
                  data-testid={`availability-dow-${d.label.toLowerCase()}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Lead time (hours)</Label>
              <Input type="number" min={0} max={72} value={f.lead_time_hours} onChange={(e) => setF({ ...f, lead_time_hours: e.target.value })} data-testid="availability-leadtime-input" />
            </div>
            <div className="space-y-1.5"><Label>Max days ahead</Label>
              <Input type="number" min={1} max={60} value={f.horizon_days} onChange={(e) => setF({ ...f, horizon_days: e.target.value })} data-testid="availability-horizon-input" />
            </div>
            <div className="space-y-1.5"><Label>Max per day</Label>
              <Input type="number" min={1} max={24} value={f.max_per_day} onChange={(e) => setF({ ...f, max_per_day: e.target.value })} data-testid="availability-maxperday-input" />
            </div>
          </div>
          <div className="flex items-center gap-3 bg-muted/50 rounded-lg p-3">
            <Switch checked={f.is_active} onCheckedChange={(v) => setF({ ...f, is_active: v })} data-testid="availability-active-switch" />
            <div><p className="text-sm font-medium">Live</p><p className="text-xs text-muted-foreground">When paused, visitors see a message that this link isn't available right now.</p></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="availability-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}{isNew ? "Create link" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
